import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { problemKind } from '../../shared/problem-text';
import { useProblemText } from '../../shared/use-problem-text';
import { Btn, Note } from '../admin-kit';

/**
 * Laden, mislukt laden en een foutmelding bovenaan, in de vorm van het nieuwe beheer (BHR-5).
 *
 * De melding van de kit (`useAdminToast`) is een bevestiging met een groen vinkje; een fout daarin
 * zou het tegendeel zeggen van wat het icoon zegt. Daarom staat een fout die bij geen paneel hoort,
 * als rood vlak bovenaan de pagina, tot de volgende handeling.
 */

/** Een kaartvulling terwijl de lijst laadt. */
export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="a-card-pad a-muted" role="status">
      {t('common.loading')}
    </div>
  );
}

/**
 * Ophalen mislukt: zeggen wat er is, en opnieuw kunnen proberen.
 *
 * Met `error` zegt de melding waarom (FBK-2): een ontbrekend recht noemt het recht en heeft geen
 * knop "Opnieuw proberen" - opnieuw proberen lost dat niet op.
 */
export function LoadError({ onRetry, error }: { onRetry: () => void; error?: unknown }) {
  const { t } = useTranslation();
  const text = useProblemText();
  const denied = problemKind(error) === 'denied';
  return (
    <div className="a-card-pad">
      <Note tone="danger" role="alert">
        {error === undefined
          ? t('beheer.communicatie.loadFailed')
          : text(error, t('beheer.communicatie.loadFailed'))}
      </Note>
      {denied ? null : (
        <div>
          <Btn variant="sec" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Btn>
        </div>
      )}
    </div>
  );
}

/** Een fout bovenaan de pagina: `show(tekst)`, `clear()`, en `element` ergens in de pagina. */
export function usePageError() {
  const [message, setMessage] = useState<ReactNode | null>(null);
  const show = useCallback((next: ReactNode) => setMessage(next), []);
  const clear = useCallback(() => setMessage(null), []);
  const element = message ? (
    <Note tone="danger" role="alert" testId="page-error">
      {message}
    </Note>
  ) : null;
  return { show, clear, element };
}
